import { Brick, registerBrick, Config } from "olympe";
import { executeLambda } from "../../node/BackendData/LambdaExecutor";
import { generateEmailContents } from "./PCTEmailTemplate";
import { getLogger } from "@olympeio/core";

export default class PCTNotification extends Brick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $ - The Olympe framework context
     * @param {*} ps_name - Lambda name for fetching PS info
     * @param {*} buyersName - Lambda name for fetching buyer info
     * @param {*} getEventDetails - Lambda name for fetching event details
     * @param {*} getEventDates - Lambda name for fetching event dates
     * @param {*} sendEmail - Lambda name for sending emails
     * @param {function()} forwardEvent - Callback to forward event after emails are sent
     * @param {function(string)} setEmail - Setter function for setting the email
     */
    async update($, [ps_name, buyersName, getEventDetails, getEventsDates, sendEmail], [forwardEvent, setEmail]) {

        const blacklistEmail = Config.getParameter('pctBlacklist');
        const logger = getLogger('PCTNotification'); // Initialize logger

        logger.info(`PCTNotification - Started at ${new Date(Date.now()).toISOString()}`);

        try {
            // 1. Fetch PS and Buyer Emails
            const ps_nameResult = await this.getLambdaResult($, ps_name, "PCTGetPSAndBuyerNames", "ps_name", "ps_email");
            const buyersNameResult = await this.getLambdaResult($, buyersName, "PCTGetPSAndBuyerNames", "buyer_name", "buyer_email");

            // Parse and combine results
            const uniqueData = this.getUniqueEmailsAndNames(ps_nameResult, buyersNameResult, blacklistEmail);
            
            // 2. Filter users by High/Severe risk events
            const userData = await this.getFilteredUserData($, uniqueData, getEventDetails);

            // Log if no user data is found to send emails
            if (userData.length === 0) {
                logger.info("PCTNotification - No users with High/Severe risk events found. No emails to send.");
                return;
            }

            // 3. Fetch event IDs for users and get recent event dates
            const emailContentData = await this.getEventDatesForUsers($, userData, getEventsDates);

            // Log if no event dates found for any user
            if (emailContentData.length === 0) {
                logger.info("PCTNotification - No recent events found for users. No emails will be sent.");
            } 
            else {
                // 4. Format and send emails
                await this.sendEmails($, emailContentData, sendEmail, logger);
            }
        } catch (error) {
            logger.error("PCTNotification - An error occurred during the update process: ", error);
        }
        finally {
          logger.info(`PCTNotification - Completed at ${new Date(Date.now()).toISOString()}`);
          
          // Forward events after sending emails
          forwardEvent();
        }
    }

    /* Helper function to execute a Lambda and return its result.
     */
    async getLambdaResult($, lambdaName, actionName, userKey, emailKey) {
        const result = await executeLambda($, lambdaName, [actionName, userKey, emailKey]);
        return JSON.parse(result);
    }

    /* Combine and filter unique email and name data.
     */
    getUniqueEmailsAndNames(psNamesArray, buyersNamesArray, blacklistEmail) {
        const seenEmails = new Set();
        const uniqueData = [];

        if(!blacklistEmail) {
          blacklistEmail = [];
        }

        // Combine PS and Buyer data and filter out duplicates and blacklisted emails
        for (const item of [...psNamesArray, ...buyersNamesArray]) {
            const emailId = item.ps_email || item.buyer_email;
            const userName = item.ps_name || item.buyer_name;

            if (emailId && userName && !seenEmails.has(emailId) && !blacklistEmail.includes(emailId)) {
                seenEmails.add(emailId);
                uniqueData.push({ userName, emailId });
            }
        }
        getLogger('PCTNotification').info('PCTNotification - Unique email and name Data: ', JSON.stringify(uniqueData));
        return uniqueData;
    }

    /* Fetches event details for users and filters by High/Severe risk levels.*/

    async getFilteredUserData($, uniqueData, getEventDetails) {
        const userData = [];
        // Fetch event details for each user
        for (const person of uniqueData) {
            const bodyResult = await executeLambda($, getEventDetails, ["PCTGetEventDetails", person.userName, person.emailId]);
          const eventDetails = JSON.parse(bodyResult);

            // Only keep users with events
            if (eventDetails.length > 0) {
                userData.push({
                    userName: person.userName,
                    emailId: person.emailId,
                    eventDetails: eventDetails
                });
            }
        }
        getLogger('PCTNotification').info('PCTNotification - User Data for High/Severe risk levels: ', JSON.stringify(userData)); 
        return userData;
    }


    /* Fetches event dates for users and appends them to the event details. */
    
    async  getEventDatesForUsers($, userData, getEventsDates) {
      const reqEmailCont = [];
      const removeLeadingZeros = (str) => str.replace(/^0+/, '');
     
      // Loop through each user in userData
      for (const user of userData) {
        const filters = [];
        const uniqueEventVendorPairs = new Set();
        const filteredEventList = [];
        let count = 0;
        // Step 1: Collect unique event-vendor pairs and build filters
        for (const detail of user.eventDetails) {
          const vendorSite = removeLeadingZeros(detail.vendor_site);
          const events = JSON.parse(detail.res_event_details)
            .filter(event => event.event_bulletins_severity === 'High' || event.event_bulletins_severity === 'Severe');
     
          events.forEach(event => {
            const uniqueKey = `${event.event_id}-${vendorSite}`;
     
            // Only add to filters if this event-vendor combination is unique
            if (!uniqueEventVendorPairs.has(uniqueKey)) {
              uniqueEventVendorPairs.add(uniqueKey);
     
              // Add filters for Lambda call and collect event for later processing
              filters.push(
                { "field": "event_id", "value": event.event_id, "group": count },
                { "field": "vendorSiteId", "value": vendorSite, "group": count }
              );
              filteredEventList.push({ ...event, vendorSite });
              count ++;
            }
          });
        }
        // Step 2: Skip Lambda call if no unique filters
        if (filters.length === 0) continue;
     
        // Step 3: Make Lambda call to get dates for the unique event-vendor pairs of this user
        const dateResponse = await executeLambda($, getEventsDates, [{ distinct: true, filters, useDisjunctiveForm: true }]);
        const dates = JSON.parse(dateResponse).results;
     
        // Step 4: Filter events that happened in the last 24 hours
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - 1);
        const recentDates = dates.filter(event => new Date(event.eventDate) >= cutoffDate);
     
        // Step 5: Match recent dates with events and prepare email content
        const userEventDetails = recentDates.map(recent => {
          const matchingEvent = filteredEventList.find(event =>
            event.event_id === recent.event_id &&
            event.vendorSite === recent.vendorSiteId
          );
          if (matchingEvent) {
            // Attach the event date to the event details
            matchingEvent.event_last_modified_date = recent.eventDate;
            return matchingEvent;
          }
        }).filter(Boolean); // Filter out any undefined entries
     
        // Step 6: Add the user's email content if relevant events exist
        if (userEventDetails.length > 0) {
          reqEmailCont.push({
            toEmailID: user.emailId,
            userName: user.userName,
            eventDetails: userEventDetails,
          });
        }
      }
     
      // Step 7: Log and return the final email content
      if (reqEmailCont.length === 0) {
        getLogger('PCTNotification').info('PCTNotification - No email to send as no valid event date found');
        return [];
      }
      getLogger('PCTNotification').info('PCTNotification - Email Content: ', JSON.stringify(reqEmailCont));
      return reqEmailCont;
    }

    /* Send emails to users with event details.
     */
    async sendEmails($, emailContentData, sendEmail, logger) {
        // For each user, generate the email content and send it
        for (const user of emailContentData) {
             // Generate the email content
            const emailContent = generateEmailContents(user);
            user.emailBody = emailContent;

            try {
                 // Send the email using the Lambda
                await executeLambda($, sendEmail, [user.toEmailID, emailContent]);
                getLogger('PCTNotification').info(`PCTNotification - PCT Email sent successfully to ${user.toEmailID}`);
            } catch (error) {
                // Log error if email sending fails
                getLogger('PCTNotification').error(`PCTNotification - Failed to send PCT email to ${user.toEmailID}: `, error);
            }
        }
    }
}



registerBrick("019246f06f11bb08fffd", PCTNotification);
